import { Injectable } from '@angular/core';
import { AuthTokenDecoded } from '../model/auth-token';
import decodeToken from '../utils/decodeToken';

const TIME_TO_CLEAR = 4600000;

export enum PersistStorageKeys {
  Store = 'store',
  StoreVersion = 'storeVersion',
  FormsTravelInPl = 'formsTravelInPl',
  FormsTripAbroad = 'formsTripAbroad',
  FormsResignation = 'formsResignation',
  FormsAgreements = 'formsAgreements',
  FormsPolicyholder = 'formsPolicyholder',
  FormsBackOfficeSection = 'formsBackOfficeSection',
  FormsAgentCommissionSplits = 'formsAgentCommissionSplits',
  CoverageInformationClosed = 'coverageInformationClosed',
  SessionID = 'sessionID',
  AgentNumber = 'agentNumber',
  AgentCategory = 'agentcategory',
  GivenName = 'givenname',
  FamilyName = 'familyname',
  DiscountCode = 'discountcode',
  AffiliateNumber = 'affiliateNumber',
  Path = 'path',
  LastUpdated = 'lastUpdated',
  Created = 'created',
  SessionToken = 'sessionToken',
  SessionTokenGrantType = 'sessionTokenGrantType',
  SessionTokenClientId = 'sessionTokenClientId',
  SessionTokenClientSecret = 'sessionTokenClientSecret',
  AppLoaded = 'appLoaded',
  EntryUrl = 'entryUrl',
  IsRemote = 'isRemote',
  TrackingDebug = 'trackingDebug',
  AgentPhone = 'agentPhone',
  AgentMail = 'agentMail',
  ProcesKind = 'procesKind',
  Country = 'country',
  DateFrom = 'dateFrom',
  DateTo = 'dateTo',
  NumberOfInsureds = 'numberOfInsureds',
  InsuredBirthYear = 'insuredBirthYear',
  PackageName = 'packageName',
  Partner = 'partner',
  Region = 'region',
}

type StorageType = 'persist' | 'session';

@Injectable({
  providedIn: 'root',
})
export class PersistStorageService {
  persistStore = localStorage;
  sessionStore = sessionStorage;

  constructor() {
    this.updateCreatedDate();
    this.checkClearPersistStore();
  }

  onAppInit(): void {
    this.checkClearPersistStore();
    this.setAgentData();
  }

  getString(key: PersistStorageKeys, type?: StorageType): string | null {
    switch (type) {
      case 'persist':
        return this.persistStore.getItem(key);
      case 'session':
        return this.sessionStore.getItem(key);
      default:
        return this.persistStore.getItem(key) || this.sessionStore.getItem(key);
    }
  }

  getJson<T = any>(key: PersistStorageKeys, type?: StorageType): T | null {
    const value: string | null = this.getString(key, type);

    return value ? (JSON.parse(value) as T) : null;
  }

  getBoolean(key: PersistStorageKeys, type?: StorageType): boolean {
    return this.getString(key, type) === 'true';
  }

  getDateTime(key: PersistStorageKeys, type?: StorageType): Date | null {
    const value = this.getString(key, type);

    return value ? new Date(value) : null;
  }

  setString(key: PersistStorageKeys, value: string, type: StorageType = 'persist') {
    this.updateLastUpdatedDate();

    switch (type) {
      case 'persist':
        this.persistStore.setItem(key, value);
        break;
      case 'session':
        this.sessionStore.setItem(key, value);
    }
  }

  setJson(key: PersistStorageKeys, value: any, type: StorageType = 'persist') {
    this.updateLastUpdatedDate();

    switch (type) {
      case 'persist':
        this.persistStore.setItem(key, JSON.stringify(value));
        break;
      case 'session':
        this.sessionStore.setItem(key, JSON.stringify(value));
        break;
    }
  }

  setBoolean(key: PersistStorageKeys, value: boolean, type: StorageType = 'persist') {
    this.updateLastUpdatedDate();

    switch (type) {
      case 'persist':
        this.persistStore.setItem(key, value ? 'true' : 'false');
        break;
      case 'session':
        this.sessionStore.setItem(key, value ? 'true' : 'false');
        break;
    }
  }

  setDateTime(key: PersistStorageKeys, value: Date, type: StorageType = 'persist') {
    this.updateLastUpdatedDate();

    switch (type) {
      case 'persist':
        this.persistStore.setItem(key, value.toISOString());
        break;
      case 'session':
        this.sessionStore.setItem(key, value.toISOString());
        break;
    }
  }

  removeItem(key: PersistStorageKeys, type: StorageType = 'persist') {
    switch (type) {
      case 'persist':
        this.persistStore.removeItem(key);
        break;
      case 'session':
        this.sessionStore.removeItem(key);
        break;
    }
  }

  removeAll(type?: StorageType | 'forms') {
    switch (type) {
      case 'persist':
        this.persistStore.clear();
        break;
      case 'session':
        this.sessionStore.clear();
        break;
      case 'forms':
        this.removeItem(PersistStorageKeys.DiscountCode);
        this.removeItem(PersistStorageKeys.FormsTravelInPl);
        this.removeItem(PersistStorageKeys.FormsTripAbroad);
        this.removeItem(PersistStorageKeys.FormsResignation);
        this.removeItem(PersistStorageKeys.FormsAgreements);
        this.removeItem(PersistStorageKeys.FormsPolicyholder);
        this.removeItem(PersistStorageKeys.FormsBackOfficeSection);
        this.removeItem(PersistStorageKeys.FormsAgentCommissionSplits);
        break;
      default:
        this.persistStore.clear();
        this.sessionStore.removeItem(PersistStorageKeys.SessionID);
        this.sessionStore.removeItem(PersistStorageKeys.AppLoaded);
        this.sessionStore.removeItem(PersistStorageKeys.EntryUrl);
        this.sessionStore.removeItem(PersistStorageKeys.AffiliateNumber);
        this.sessionStore.removeItem(PersistStorageKeys.Path);
    }
  }

  private updateCreatedDate() {
    if (!this.persistStore.getItem(PersistStorageKeys.Created)) {
      this.persistStore.setItem(PersistStorageKeys.Created, new Date().toISOString());
    }
  }

  private updateLastUpdatedDate() {
    this.persistStore.setItem(PersistStorageKeys.LastUpdated, new Date().toISOString());
  }

  private checkClearPersistStore() {
    const lastUpdated = this.getDateTime(PersistStorageKeys.LastUpdated);

    if (lastUpdated && new Date().getTime() - lastUpdated.getTime() > TIME_TO_CLEAR) {
      this.removeAll('persist');
      location.reload();
    }
  }

  private setAgentData(): void {
    const accessToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sessionToken');

    if (accessToken) {
      const decodedToken = decodeToken(accessToken) as AuthTokenDecoded;
      this.setString(PersistStorageKeys.AgentCategory, String(decodedToken.agent_category), 'session');
      this.setString(PersistStorageKeys.GivenName, String(decodedToken.given_name), 'session');
      this.setString(PersistStorageKeys.FamilyName, String(decodedToken.family_name), 'session');
      this.setString(PersistStorageKeys.AgentMail, String(decodedToken.agent_email), 'session');
      if (decodedToken.agent_phone) {
        this.setString(PersistStorageKeys.AgentPhone, String(decodedToken.agent_phone), 'session');
      }
      const storedAgentNumber = String(decodedToken.az_agent_number) === 'undefined' ? undefined : String(decodedToken.az_agent_number);
      if (storedAgentNumber) {
        this.setString(PersistStorageKeys.AgentNumber, storedAgentNumber, 'session');
      }
    }
  }
}
